import {
  buildOpinionDateAxisLabel,
  buildOpinionDateAxisPointerLabel,
  DATE_AXIS_LABEL_MARGIN,
  DATE_AXIS_LABEL_MARGIN_WITH_EVENT,
} from './buildOpinionDateAxisPointerLabel';

export {
  DATE_AXIS_LABEL_MARGIN,
  DATE_AXIS_LABEL_MARGIN_WITH_EVENT,
} from './buildOpinionDateAxisPointerLabel';

function getAxisEventLabel(
  eventList: Record<string, { count?: number }>,
  key: string,
): string {
  const value = eventList?.[key]?.count || 0;
  if (value === 0) return '';
  return value > 99 ? '99+' : String(value);
}

export const EVENT_AXIS_INDEX = 0;
export const EVENT_AXIS_LABEL_WIDTH = 20;
export const EVENT_AXIS_LABEL_HEIGHT = 14;
export const EVENT_AXIS_LABEL_MARGIN = -6;
export const EVENT_AXIS_OFFSET = 15;

const EVENT_AXIS_LABEL_STYLE = {
  width: EVENT_AXIS_LABEL_WIDTH,
  height: EVENT_AXIS_LABEL_HEIGHT,
  align: 'center',
  verticalAlign: 'middle',
  lineHeight: 14,
  fontSize: 10,
  borderRadius: 7,
  padding: 0,
  shadowBlur: 2,
  shadowColor: 'rgba(0, 0, 0, 0.3)',
};

export function getEventXAxisAxisLabel(
  eventList: Record<string, { count?: number }>,
  highlightKey?: string,
) {
  return {
    margin: EVENT_AXIS_LABEL_MARGIN,
    interval: 0,
    rich: {
      eventShow: {
        ...EVENT_AXIS_LABEL_STYLE,
        color: 'rgb(131, 134, 151)',
        backgroundColor: '#fff',
      },
      eventHighlight: {
        ...EVENT_AXIS_LABEL_STYLE,
        color: '#FFFFFF',
        backgroundColor: '#326FF0',
      },
      eventHide: {},
    },
    splitLine: { show: true },
    formatter: (value: string) => {
      const eventNumber = getAxisEventLabel(eventList, value);
      const eventCss = eventNumber ? 'eventShow' : 'eventHide';
      if (highlightKey === value) {
        return `{eventHighlight|${eventNumber}}`;
      }
      return `{${eventCss}|${eventNumber}}`;
    },
  };
}

function getEventAxisSpacerLabel() {
  return {
    margin: EVENT_AXIS_LABEL_MARGIN,
    interval: 0,
    rich: {
      eventAxisSpacer: {
        ...EVENT_AXIS_LABEL_STYLE,
        color: 'transparent',
        backgroundColor: 'transparent',
      },
    },
    formatter: () => '{eventAxisSpacer|}',
  };
}

function normalizeBaseXAxis(lineOption?: IRecord | null): IRecord {
  const xAxis = lineOption?.xAxis;
  if (Array.isArray(xAxis)) {
    return xAxis[xAxis.length - 1] || xAxis[0] || {};
  }
  return xAxis || {};
}

/**
 * 舆情事件轴：底部双 x 轴（事件数量 + 日期）。
 * 关闭事件标注时仍保留占位轴，确保图注与日期间距与开启时一致（20px）。
 */
export function buildDualEventXAxisList({
  lineOption,
  xAxisData,
  chartRenderEventList = {},
  showEventAxis,
}: {
  lineOption?: IRecord | null;
  xAxisData: string[];
  chartRenderEventList?: Record<string, { count?: number }>;
  showEventAxis: boolean;
}): IRecord[] {
  const baseDateXAxis = normalizeBaseXAxis(lineOption);
  const dateAxisLabel = buildOpinionDateAxisLabel({
    baseAxisLabel: baseDateXAxis?.axisLabel || {},
    showEventAxis: true,
  });

  const eventAxisLine = {
    ...(baseDateXAxis?.axisLine || {}),
    lineStyle: { color: '#D8DAE4' },
    onZero: false,
  };

  const eventAxis = showEventAxis
    ? {
        show: true,
        type: 'category',
        id: 'eventDetails',
        data: xAxisData,
        position: 'bottom',
        triggerEvent: true,
        axisTick: { show: false },
        boundaryGap: baseDateXAxis?.boundaryGap ?? true,
        offset: EVENT_AXIS_OFFSET,
        axisLabel: getEventXAxisAxisLabel(chartRenderEventList),
        axisPointer: {
          label: { show: false },
        },
        axisLine: eventAxisLine,
      }
    : {
        show: true,
        type: 'category',
        id: 'eventAxisSpacer',
        data: xAxisData,
        position: 'bottom',
        axisTick: { show: false },
        boundaryGap: baseDateXAxis?.boundaryGap ?? true,
        offset: EVENT_AXIS_OFFSET,
        axisLabel: getEventAxisSpacerLabel(),
        axisPointer: {
          label: { show: false },
        },
        axisLine: eventAxisLine,
      };

  return [
    eventAxis,
    {
      ...baseDateXAxis,
      id: 'dateAxis',
      type: 'category',
      data: xAxisData,
      position: 'bottom',
      boundaryGap: baseDateXAxis?.boundaryGap ?? true,
      axisLine: {
        ...(baseDateXAxis?.axisLine || {}),
        lineStyle: { color: '#D8DAE4' },
        onZero: false,
      },
      axisLabel: dateAxisLabel,
      axisPointer: {
        ...(baseDateXAxis?.axisPointer || {}),
        label: buildOpinionDateAxisPointerLabel(
          DATE_AXIS_LABEL_MARGIN_WITH_EVENT,
        ),
      },
    },
  ];
}

export function applySeriesXAxisIndex(
  series: IRecord[] = [],
  xAxisIndex = 0,
): IRecord[] {
  return series.map(item => ({
    ...item,
    xAxisIndex,
  }));
}
